import React, { useEffect, useMemo, useState } from 'react';
import './TopBar.css';
import { useNavigate } from 'react-router-dom';
import { LogOut, Users, Gavel, ArrowLeftRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import useFantasyDataSyncing from '../hooks/useFantasyDataSyncing';
import { getAndResolveMyTradesBadgeCount } from '../firebase/publicTradesService';
import ThemeToggle from './ThemeToggle';
import Tooltip from './Tooltip';

export default function TopBar() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { syncing } = useFantasyDataSyncing();

  const [imgError, setImgError] = useState(false);
  const [tradesBadgeCount, setTradesBadgeCount] = useState(0);

  // Conta i propri scambi pubblici chiusi e non ancora visti (vedi
  // publicTradesService: risolve anche i sondaggi scaduti nel frattempo).
  // TopBar è montata su ogni pagina, quindi si aggiorna naturalmente ad ogni
  // cambio pagina senza bisogno di polling.
  useEffect(() => {
    if (!user?.uid) {
      setTradesBadgeCount(0);
      return;
    }
    getAndResolveMyTradesBadgeCount(user.uid)
      .then(setTradesBadgeCount)
      .catch(() => setTradesBadgeCount(0));
  }, [user?.uid]);

  const initials = useMemo(() => {
    const base = user?.displayName || user?.email || 'U';
    return base
      .split(' ')
      .slice(0, 2)
      .map((x) => x[0]?.toUpperCase())
      .join('');
  }, [user]);

  return (
    <header className="topbar glass">
      {/* LEFT */}
      <div className="topbar__brand" onClick={() => navigate('/')} role="button" tabIndex={0}>

        {/* LOGO APP */}
        <div className="topbar__appLogo">
          <img src="/logoFantApp_512x512.png" alt="FantApp Logo" />
        </div>

        <div className="topbar__user">
          <div className="topbar__avatar">
            {!imgError && user?.photoURL ? (
              <img
                src={user.photoURL}
                alt={user?.displayName || 'User avatar'}
                onError={() => setImgError(true)}
              />
            ) : (
              <span>{initials}</span>
            )}
          </div>

          <div className="topbar__userText">
            <strong>{user?.displayName || 'Manager'}</strong>
            <span>{user?.email || 'Google account'}</span>
          </div>
        </div>
      </div>

      {/* CENTER — wordmark, centrato sull'intera barra (non sulla sola
          colonna centrale del grid) tramite position:absolute */}
      <span className="topbar__brandTitle" aria-hidden="true">FantApp!</span>

      {/* RIGHT */}
      <div className="topbar__actions">
        <ThemeToggle />
        <button onClick={() => navigate('/players')} aria-label="Giocatori">
          <Users className="topbar__actionIcon" size={20} />
        </button>
        <Tooltip
          as="button"
          onClick={() => navigate('/aste')}
          aria-label="Aste"
          disabled={syncing}
          content={syncing ? 'Sincronizzazione dati in corso…' : 'Aste'}
        >
          <Gavel className="topbar__actionIcon" size={20} />
        </Tooltip>
        <Tooltip as="button" onClick={() => navigate('/scambi')} aria-label="Scambi" content="Scambi" className="topbar__actionBtnWithBadge">
          <ArrowLeftRight className="topbar__actionIcon" size={20} />
          {tradesBadgeCount > 0 && (
            <span className="topbar__actionBadge">{tradesBadgeCount > 9 ? '9+' : tradesBadgeCount}</span>
          )}
        </Tooltip>
        <button onClick={logout} aria-label="Logout">
          <LogOut size={18} />
        </button>
      </div>
    </header>
  );
}
